import { useEffect, useState, useMemo, useCallback } from "react";
import { Trash, Clock, SignOut, MagnifyingGlass, MapPin, Cloud, PencilSimple, ChartBar, Sparkle } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/lib/AuthProvider";
import { GoogleAdsBadge } from "@/components/GoogleAdsBadge";
import { adsAuditsList, adsAuditDelete, adsAuditRename, errText } from "@/lib/api";

const timeAgo = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const s = Math.floor((Date.now() - d.getTime()) / 1000);
    if (s < 60) return `${s}s`;
    if (s < 3600) return `${Math.floor(s / 60)}m`;
    if (s < 86400) return `${Math.floor(s / 3600)}h`;
    return `${Math.floor(s / 86400)}d`;
};

const KIND_META = {
    serp: {
        label: "SERP",
        cls: "bg-klein text-white",
        icon: MagnifyingGlass,
    },
    ads_search_terms: {
        label: "Terms",
        cls: "bg-emerald-100 text-emerald-900 border border-emerald-300",
        icon: Cloud,
    },
    ads_geographic: {
        label: "Geo",
        cls: "bg-amber-100 text-amber-900 border border-amber-300",
        icon: MapPin,
    },
    ads_auction_insights: {
        label: "Auction",
        cls: "bg-red-100 text-red-900 border border-red-300",
        icon: ChartBar,
    },
    ads_distiller: {
        label: "Distill",
        cls: "bg-yellow-100 text-yellow-900 border border-yellow-400",
        icon: Sparkle,
    },
};

/**
 * Sidebar now hosts a mixed history list:
 *  - SERP searches (existing) — props.items, onSelect, onDelete
 *  - Ads audits (search terms + geographic) — fetched here, dispatched via props.onOpenAudit
 */
export const SidebarHistory = ({
    items,
    activeId,
    onSelect,
    onDelete,
    onOpenAudit,        // (audit) => void — main page routes to Performance + loads rows
    onAdsAuditsRefresh, // optional imperative refresh trigger (exposed by ref)
    refreshTick,        // change this from parent to force refetch
}) => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [adsAudits, setAdsAudits] = useState([]);
    const [renamingId, setRenamingId] = useState(null);
    const [renameValue, setRenameValue] = useState("");

    const loadAudits = useCallback(async () => {
        try {
            const list = await adsAuditsList();
            setAdsAudits(list || []);
        } catch {
            setAdsAudits([]);
        }
    }, []);

    useEffect(() => {
        loadAudits();
    }, [loadAudits, refreshTick]);

    const handleLogout = async () => {
        await logout();
        navigate("/login", { replace: true });
    };

    // Merge + sort SERP items and Ads audits by created_at desc.
    const merged = useMemo(() => {
        const serp = (items || []).map((it) => ({
            _id: it.id,
            _kind: "serp",
            _title: it.business_type,
            _subtitle: `${it.city || ""}${it.country_label || it.country?.toUpperCase() ? " · " + (it.country_label || it.country?.toUpperCase()) : ""}`,
            _createdAt: it.created_at,
            _raw: it,
        }));
        const audits = adsAudits.map((a) => ({
            _id: a.id,
            _kind: a.kind,
            _title: a.label || `${a.kind} audit`,
            _subtitle: `${a.row_count} rows · ${a.customer_id}`,
            _createdAt: a.created_at,
            _raw: a,
        }));
        return [...serp, ...audits].sort((a, b) => {
            const ta = new Date(a._createdAt || 0).getTime();
            const tb = new Date(b._createdAt || 0).getTime();
            return tb - ta;
        });
    }, [items, adsAudits]);

    const handleItemClick = (item) => {
        if (item._kind === "serp") {
            onSelect?.(item._id);
        } else {
            onOpenAudit?.(item._raw);
        }
    };

    const handleItemDelete = async (item, e) => {
        e.stopPropagation();
        if (item._kind === "serp") {
            onDelete?.(item._id);
        } else {
            try {
                await adsAuditDelete(item._id);
                setAdsAudits((prev) => prev.filter((a) => a.id !== item._id));
                toast.success("Deleted audit");
            } catch (err) {
                toast.error(errText(err, "Delete failed"));
            }
        }
    };

    const startRename = (item, e) => {
        e.stopPropagation();
        setRenamingId(item._id);
        setRenameValue(item._title || "");
    };

    const submitRename = async (e) => {
        e.stopPropagation();
        if (!renamingId) return;
        const label = renameValue.trim();
        if (!label) return setRenamingId(null);
        try {
            await adsAuditRename(renamingId, label);
            setAdsAudits((prev) => prev.map((a) => (a.id === renamingId ? { ...a, label } : a)));
            toast.success("Renamed");
        } catch (err) {
            toast.error(errText(err, "Rename failed"));
        } finally {
            setRenamingId(null);
        }
    };

    return (
        <aside
            data-testid="sidebar-history"
            className="hidden lg:flex flex-col w-72 flex-shrink-0 border-r border-neutral-200 bg-sidebar h-screen"
        >
            <div className="px-5 py-6 border-b border-neutral-200">
                <div className="flex items-center gap-2">
                    <Clock size={16} weight="bold" />
                    <h2 className="font-heading text-sm font-black uppercase tracking-widest">
                        History
                    </h2>
                </div>
                <p className="mono-label mt-2">SERP + Ads audits</p>
            </div>

            <div className="flex-1 overflow-y-auto thin-scroll">
                {merged.length === 0 ? (
                    <div className="px-5 py-8">
                        <p className="mono-label">No history yet.</p>
                        <p className="text-sm text-neutral-500 mt-2 leading-relaxed">
                            Run a competitor scan or fetch a Google Ads audit to build history.
                        </p>
                    </div>
                ) : (
                    <ul>
                        {merged.map((item) => {
                            const active = item._id === activeId;
                            const meta = KIND_META[item._kind] || KIND_META.serp;
                            const isRenaming = renamingId === item._id;
                            const canRename = item._kind !== "serp";
                            return (
                                <li
                                    key={`${item._kind}-${item._id}`}
                                    data-testid={`history-item-${item._id}`}
                                    className={`group border-b border-neutral-200 px-5 py-4 cursor-pointer transition-colors duration-150 ${
                                        active
                                            ? "bg-white border-l-2 border-l-klein"
                                            : "hover:bg-white"
                                    }`}
                                    onClick={() => !isRenaming && handleItemClick(item)}
                                >
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-1.5 mb-1">
                                                <span
                                                    className={`px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-widest font-bold ${meta.cls}`}
                                                >
                                                    {meta.label}
                                                </span>
                                            </div>
                                            {isRenaming ? (
                                                <input
                                                    data-testid={`history-rename-input-${item._id}`}
                                                    autoFocus
                                                    value={renameValue}
                                                    onChange={(e) => setRenameValue(e.target.value)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter") submitRename(e);
                                                        else if (e.key === "Escape") setRenamingId(null);
                                                    }}
                                                    onBlur={submitRename}
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="w-full text-sm font-heading font-bold text-ink bg-white border border-ink px-1 py-0.5 focus:outline-none"
                                                />
                                            ) : (
                                                <p className="font-heading font-bold text-sm text-ink truncate">
                                                    {item._title}
                                                </p>
                                            )}
                                            <p className="text-xs text-neutral-500 mt-0.5 truncate">
                                                {item._subtitle}
                                            </p>
                                            <p className="mono-label mt-2">
                                                {timeAgo(item._createdAt)} ago
                                            </p>
                                        </div>
                                        <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                                            {canRename && !isRenaming && (
                                                <button
                                                    data-testid={`history-rename-${item._id}`}
                                                    onClick={(e) => startRename(item, e)}
                                                    className="text-neutral-400 hover:text-ink"
                                                    aria-label="Rename"
                                                >
                                                    <PencilSimple size={13} weight="bold" />
                                                </button>
                                            )}
                                            <button
                                                data-testid={`history-delete-${item._id}`}
                                                onClick={(e) => handleItemDelete(item, e)}
                                                className="text-neutral-400 hover:text-red-500"
                                                aria-label="Delete history"
                                            >
                                                <Trash size={14} weight="bold" />
                                            </button>
                                        </div>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>

            <div className="px-5 py-4 border-t border-neutral-200 space-y-3">
                <GoogleAdsBadge onChange={() => loadAudits()} />
                {user && (
                    <div className="flex items-center gap-2">
                        {user.picture ? (
                            <img
                                src={user.picture}
                                alt=""
                                referrerPolicy="no-referrer"
                                className="w-7 h-7 rounded-full border border-neutral-300"
                            />
                        ) : (
                            <div className="w-7 h-7 rounded-full bg-ink text-white flex items-center justify-center font-bold text-xs">
                                {(user.name || user.email || "?")[0].toUpperCase()}
                            </div>
                        )}
                        <div className="flex-1 min-w-0">
                            <div
                                className="text-xs font-bold text-ink truncate"
                                data-testid="sidebar-user-name"
                                title={user.email}
                            >
                                {user.name || user.email}
                            </div>
                            <div className="text-[10px] text-neutral-500 font-mono truncate">
                                {user.email}
                            </div>
                        </div>
                        <button
                            data-testid="btn-signout"
                            onClick={handleLogout}
                            title="Sign out"
                            className="w-7 h-7 flex items-center justify-center border border-neutral-300 hover:border-ink hover:bg-ink hover:text-white transition"
                        >
                            <SignOut size={12} weight="bold" />
                        </button>
                    </div>
                )}
                <p className="mono-label">powered by dataforseo + claude vision</p>
            </div>
        </aside>
    );
};
